import React from 'react';

import { selectClassName, SELECT_BACKGROUND } from '../FormField';

import { Choice, ChoicesOption, Group, RadioChoice, Toggle } from './OptionControls';

import MultiSelectField from 'CommonComponents/MultiSelectField';

const VERSION_COLUMNS = [
    { value: 'variance', label: () => $t('Variance') },
    { value: 'variance_pct', label: () => $t('Variance %') },
    { value: 'committed', label: () => $t('Committed') },
    { value: 'uncommitted', label: () => $t('Uncommitted') },
    { value: 'actuals', label: () => $t('Actuals to date') },
    { value: 'risks', label: () => $t('AFC risks') },
    { value: 'opportunities', label: () => $t('AFC opportunities') },
    { value: 'afc', label: () => $t('AFC') },
    { value: 'afc_vs_budget', label: () => $t('AFC vs budget') },
    { value: 'status', label: () => $t('Status') },
];

const MOVEMENT_COLUMNS = [
    { value: 'le', label: () => $t('Latest estimate') },
    { value: 'committed', label: () => $t('Committed') },
    { value: 'uncommitted', label: () => $t('Uncommitted') },
    { value: 'afc_items', label: () => $t('AFC items (net)') },
    { value: 'status', label: () => $t('Status') },
];

/**
 * The columns shown when none are stored, as BudgetSummarySectionOptions
 * defaults them: every one but actuals to date.
 */
const DEFAULT_COLUMNS = {
    version_columns: VERSION_COLUMNS.map((column) => column.value).filter((value) => value !== 'actuals'),
    movement_columns: MOVEMENT_COLUMNS.map((column) => column.value),
};

const TOP_LIMITS = [5, 8, 10];

const resolved = (items) => items.map((item) => ({ value: item.value, label: item.label() }));

/**
 * The budget summary's options: the level and blocks of the version page,
 * the movement and changes pages, and the departments left out of all three.
 */
const BudgetSummaryOptions = ({ options, departments, readOnly, onChange }) => {
    const set = (changes) => onChange({ ...options, ...changes });

    const toggle = (key, label, hint) => (
        <Toggle label={label} hint={hint} checked={!!options[key]} disabled={readOnly} onChange={() => set({ [key]: !options[key] })} />
    );

    const choices = (key, label, items) => (
        <ChoicesOption
            sectionKey="budget_summary"
            definition={{ key, label, choices: resolved(items) }}
            value={options[key] ?? DEFAULT_COLUMNS[key]}
            disabled={readOnly}
            onChange={(value) => set({ [key]: value })}
        />
    );

    return (
        <div className="tw-flex tw-flex-col tw-mt-3 tw-ps-4 tw-border-s-2 tw-border-charcoal-15">
            <Group title={$t('Latest estimate vs budget version')}>
                <RadioChoice
                    name="mp-budget-summary-level"
                    label={$t('Rows')}
                    value={options.level}
                    choices={[
                        { value: 'department', label: $t('Department') },
                        { value: 'costcentre', label: $t('Department and cost centre') },
                    ]}
                    disabled={readOnly}
                    onChange={(level) => set({ level })}
                />
                {choices('version_columns', $t('Columns'), VERSION_COLUMNS)}
                {toggle('revenue', $t('Revenue by stream'), $t('Shown on organisation-wide packs.'))}
                {toggle('net_table', $t('Net position table'))}
                {toggle('bridge', $t('Bridge from the budget version to the latest estimate'))}
            </Group>

            <Group title={$t('Movement since last report')}>
                {toggle('movement_page', $t('Include the movement page'))}
                {options.movement_page && (
                    <>
                        {choices('movement_columns', $t('Columns'), MOVEMENT_COLUMNS)}
                        {toggle('show_prior_current', $t('Show prior and current values'), $t('Wide tables continue on a second slide.'))}
                        {toggle('contingency', $t('Contingency drawn since last report'))}
                    </>
                )}
            </Group>

            <Group title={$t('Movements since the budget version')}>
                {toggle('changes_page', $t('Include the budget changes page'))}
                {options.changes_page && (
                    <Choice
                        id="mp-budget-summary-top-limit"
                        label={$t('Largest movements shown')}
                        value={options.top_limit}
                        choices={TOP_LIMITS.map((limit) => ({ value: limit, label: $t('Top {{count}}', { count: limit }) }))}
                        disabled={readOnly}
                        onChange={(value) => set({ top_limit: value })}
                    />
                )}
            </Group>

            <Group title={$t('Excluded departments')}>
                <label className="tw-text-sm tw-text-charcoal tw-mb-0" htmlFor="mp-budget-summary-exclusions">
                    {$t('Exclude departments from all three pages')}
                </label>
                <MultiSelectField
                    inputId="mp-budget-summary-exclusions"
                    className={selectClassName}
                    backgroundColor={SELECT_BACKGROUND}
                    options={departments.map((department) => ({ value: department.value, label: department.label }))}
                    hideSelectedOptions={false}
                    clearable
                    isDisabled={readOnly}
                    value={options.excluded_departments}
                    placeholder={$t('None excluded')}
                    onChange={(selected) => set({ excluded_departments: (selected || []).map((option) => option.value) })}
                />
            </Group>
        </div>
    );
};

export default BudgetSummaryOptions;
